import {PortfolioAccountContext} from '#/components/portfolio/context/portfolio-account-context';
import {useLandAccountInfos} from '#/hooks/query/use-land-account-info';
import {useLandMarketInfos} from '#/hooks/query/use-land-market-info';
import {usePortfolioLandsAndAssets} from '#/hooks/query/use-portfolio-lands';
import {DefaultChildren} from '#/utils/utils';
import {Land, LandAccountInfo, LandMarketInfo, TransactionPortfolioValue} from '@shareland/shareland-common';
import {FunctionComponent, createContext, useContext, useEffect} from 'react';

type LandGroup = {
  land: Land;
  portfolioValue: TransactionPortfolioValue;
  landMarketInfo: LandMarketInfo | undefined;
  landAccountInfo: LandAccountInfo | undefined;
};

interface PortfolioAssetsContextValue {
  landGroups: LandGroup[];
  isLoading: {
    lands: boolean;
    marketInfo: boolean;
    accountInfo: boolean;
  };
  error: {
    lands: Error | null;
    marketInfo: Error | null;
    accountInfo: Error | null;
  };
}

export const PortfolioAssetsContext = createContext<PortfolioAssetsContextValue>({
  landGroups: [],
  isLoading: {
    lands: true,
    marketInfo: true,
    accountInfo: true,
  },
  error: {
    lands: null,
    marketInfo: null,
    accountInfo: null,
  },
});

export const PortfolioAssetsContextProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const {account} = useContext(PortfolioAccountContext);

  const portfolioLandsAndAssetsQuery = usePortfolioLandsAndAssets(account);
  const portfolioLands = portfolioLandsAndAssetsQuery.data.map(({land}) => land);
  const landMarketInfosQuery = useLandMarketInfos(portfolioLands);
  const landAccountInfosQuery = useLandAccountInfos(portfolioLands, account);

  const landGroups = portfolioLandsAndAssetsQuery.data.map(({land, portfolioValue}, index) => ({
    land,
    portfolioValue,
    landMarketInfo: landMarketInfosQuery.data[index],
    landAccountInfo: landAccountInfosQuery.data[index],
  }));

  const isLoading = {
    lands: portfolioLandsAndAssetsQuery.isLoading,
    marketInfo: landMarketInfosQuery.isLoading,
    accountInfo: landAccountInfosQuery.isLoading,
  };

  const error = {
    lands: portfolioLandsAndAssetsQuery.error,
    marketInfo: landMarketInfosQuery.error as Error,
    accountInfo: landAccountInfosQuery.error as Error,
  };

  useEffect(() => {
    landMarketInfosQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PortfolioAssetsContext.Provider
      value={{
        landGroups,
        isLoading,
        error,
      }}
    >
      {children}
    </PortfolioAssetsContext.Provider>
  );
};
